import { getPriceAvailability } from '@/hooks/usePriceDisplay';
import type { Product } from '@/types/entity/product';
import type { CtProductSnapshot } from '@/utils/server/getCtSnapshotForSku';

export interface CtSnapshotApplied {
  /** The mapped product with the authoritative price + stock merged in. */
  item: Product;
  /** True when CT returned no Standalone price for the SKU — tile shows "Unavailable". */
  priceUnavailable: boolean;
  /** True when CT returned no stock signal — tile suppresses the stock badge. */
  stockUnavailable: boolean;
}

/**
 * EWM-636 — merge an authoritative CT price + stock snapshot onto a mapped
 * `Product`, returning the merged item plus the `priceUnavailable` /
 * `stockUnavailable` flags the tile uses to gate its "Unavailable" /
 * "Out of stock" surfaces.
 *
 * Single source of truth for the snapshot → tile merge, shared by the PLP/Search
 * tile (`product-tile-with-ct`) and the home Featured Products carousel. Mirrors
 * EWM-525 / EWM-598: the resolved Standalone price (NOT the embedded variant
 * price) is what we show + charge — the embedded price is `0` for SKUs that only
 * carry a Standalone Price, which is exactly the carousel's `$0.00` bug. When the
 * snapshot carries no product discount, `discountedPrice` is cleared so any
 * Algolia/embedded discount can't render a false was/now.
 */
export const applyCtSnapshot = (item: Product, snapshot?: CtProductSnapshot | null): CtSnapshotApplied => {
  const { priceUnavailable, stockUnavailable } = getPriceAvailability(snapshot);
  const merged: Product = {
    ...item,
    ...(!priceUnavailable
      ? {
          price: (snapshot!.activePrice!.centAmount ?? 0) / 100,
          currency: (snapshot!.activePrice!.currencyCode ?? item.currency) as Product['currency'],
          discountedPrice: snapshot!.discountedPrice ? (snapshot!.discountedPrice.centAmount ?? 0) / 100 : undefined,
          // EWM-525 — authoritative `price-rrp` reference (same source as the PDP
          // pill), overriding the stale Algolia `rrp` on the mapped item; cleared
          // when the snapshot carries none. Display reference only — the reducer
          // excludes `price-rrp` from the sell-price selection, so this is never
          // the charged price.
          rrp: snapshot!.rrp ? (snapshot!.rrp.centAmount ?? 0) / 100 : undefined,
        }
      : {}),
    ...(!stockUnavailable ? { inStock: snapshot!.inStock } : {}),
  };
  return { item: merged, priceUnavailable, stockUnavailable };
};
